/**
 * Carousel — unstyled, composable carousel primitives backed by Embla.
 *
 * Use Carousel for scrollable rows, paged panes, slideshows, and marquees that
 * need carousel behavior (snap points, arrows, indicators, autoplay, or loop).
 * For plain native-scroll rows that only need edge affordance, keep using
 * GradientScrollAffordance.
 *
 * Embla measures the viewport's first child as the container and that
 * container's direct children as slides. CarouselSlide is optional: it only
 * adds default sizing and slide semantics.
 *
 * Do not put transforms or transitions on CarouselContent or CarouselSlide;
 * Embla owns those elements' transforms. Put decorative motion on an inner
 * wrapper inside each slide instead.
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";
import type {
  EmblaCarouselType,
  EmblaOptionsType,
  EmblaPluginType,
} from "embla-carousel";
import type {
  AutoScrollOptionsType,
  AutoScrollType,
} from "embla-carousel-auto-scroll";
import type {
  AutoplayOptionsType,
  AutoplayType,
} from "embla-carousel-autoplay";
// eslint-disable-next-line no-restricted-imports
import type { EmblaViewportRefType } from "embla-carousel-react";
import type { WheelGesturesPluginOptions } from "embla-carousel-wheel-gestures";

import React from "react";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cn } from "@replohq/lattice/utils/cn";
import AutoScroll from "embla-carousel-auto-scroll";
import Autoplay from "embla-carousel-autoplay";
// eslint-disable-next-line no-restricted-imports
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { useReducedMotion } from "motion/react";
import { isNullish } from "replo-utils/lib/misc";
import { useRequiredContext } from "replo-utils/react/context";

type CarouselOrientation = "horizontal" | "vertical";

type CarouselClassNameProps<ClassName extends string = string> = {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
};

type CarouselState = {
  selectedIndex: number;
  scrollSnaps: number[];
  canGoPrevious: boolean;
  canGoNext: boolean;
  /** Whether the user is actively dragging the carousel right now. */
  isDragging: boolean;
};

type CarouselContextValue = CarouselState & {
  viewportRef: EmblaViewportRefType;
  emblaApi: EmblaCarouselType | undefined;
  orientation: CarouselOrientation;
  loop: boolean;
  isScrollable: boolean;
  /** Whether pointer dragging is enabled (Embla's `watchDrag`, on by default). */
  isDraggable: boolean;
  goPrevious: () => void;
  goNext: () => void;
  goTo: (index: number) => void;
};

const CarouselContext = React.createContext<CarouselContextValue | null>(null);

const initialCarouselState: CarouselState = {
  selectedIndex: 0,
  scrollSnaps: [],
  canGoPrevious: false,
  canGoNext: false,
  isDragging: false,
};

function useCarousel() {
  return useRequiredContext(CarouselContext);
}

// `isDragging` is tracked separately via pointer events, so the Embla-derived
// state omits it and callers preserve it across selection updates.
function getCarouselSelectionState(
  emblaApi: EmblaCarouselType,
): Omit<CarouselState, "isDragging"> {
  return {
    selectedIndex: emblaApi.selectedScrollSnap(),
    scrollSnaps: emblaApi.scrollSnapList(),
    canGoPrevious: emblaApi.canScrollPrev(),
    canGoNext: emblaApi.canScrollNext(),
  };
}

// Auto-scroll/autoplay drive the transform every frame, so a manual scroll
// target is immediately overwritten unless we interrupt the plugin first. When
// `stopOnInteraction` is false the carousel is meant to keep moving, so `reset`
// lets the manual jump land and then resumes; otherwise the interaction halts it.
function interruptAutoMotionPlugin(
  plugin: AutoScrollType | AutoplayType | undefined,
) {
  if (!plugin) {
    return;
  }

  if (plugin.options.stopOnInteraction === false) {
    plugin.reset();
  } else {
    plugin.stop();
  }
}

function interruptAutoMotion(emblaApi: EmblaCarouselType) {
  const plugins = emblaApi.plugins();
  interruptAutoMotionPlugin(plugins.autoScroll);
  interruptAutoMotionPlugin(plugins.autoplay);
}

function isTextEntryTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(
    target.closest("input, textarea, select, [contenteditable='true']"),
  );
}

function getPlugins({
  autoplay,
  autoScroll,
  isScrollable,
  plugins,
  shouldReduceMotion,
  wheelGestures,
}: {
  autoplay?: boolean | AutoplayOptionsType;
  autoScroll?: boolean | AutoScrollOptionsType;
  isScrollable: boolean;
  plugins?: EmblaPluginType[];
  shouldReduceMotion: boolean;
  wheelGestures?: boolean | Partial<WheelGesturesPluginOptions>;
}) {
  const emblaPlugins: EmblaPluginType[] = [];

  // The wheel-gestures plugin has no built-in "only when scrollable" mode — once
  // installed it captures (and `preventDefault`s) wheel input even when the
  // slides fit inside the viewport. Gate it on scrollability so the wheel passes
  // through to the page when there's nothing to scroll.
  if (wheelGestures && isScrollable) {
    emblaPlugins.push(
      WheelGesturesPlugin(wheelGestures === true ? undefined : wheelGestures),
    );
  }

  if (!shouldReduceMotion && autoplay) {
    emblaPlugins.push(Autoplay(autoplay === true ? undefined : autoplay));
  }

  if (!shouldReduceMotion && autoScroll) {
    emblaPlugins.push(AutoScroll(autoScroll === true ? undefined : autoScroll));
  }

  return [...emblaPlugins, ...(plugins ?? [])];
}

// Grows the fade from 0 to `sizePixels` over the first `revealThresholdPixels` of
// scroll past an edge, then holds it. Both inputs are plain pixels, so this is
// pure number math — no resolving CSS lengths to pixels.
function getEdgeFadePixels({
  distance,
  revealThresholdPixels,
  sizePixels,
}: {
  distance: number;
  revealThresholdPixels: number;
  sizePixels: number;
}) {
  if (revealThresholdPixels <= 0) {
    return sizePixels;
  }

  const progress = Math.max(0, Math.min(1, distance / revealThresholdPixels));
  return sizePixels * progress;
}

// Embla scrolls by transforming the container rather than natively scrolling, so
// the pure-CSS scroll-driven fade that GradientScrollAffordance uses can't apply
// here. We compute the fade width in JS (pixels in, pixels out) and write it to
// the mask variables the `.carousel-scroll-fade` classes consume.
function setEdgeFadeVariables({
  emblaApi,
  isScrollable,
  revealThresholdPixels,
  sizePixels,
  viewportElement,
}: {
  emblaApi: EmblaCarouselType;
  isScrollable: boolean;
  revealThresholdPixels: number;
  sizePixels: number;
  viewportElement: HTMLElement;
}) {
  if (!isScrollable) {
    viewportElement.style.setProperty("--gsa-fade-start", "0px");
    viewportElement.style.setProperty("--gsa-fade-end", "0px");
    return;
  }

  if (emblaApi.internalEngine().options.loop) {
    viewportElement.style.setProperty("--gsa-fade-start", `${sizePixels}px`);
    viewportElement.style.setProperty("--gsa-fade-end", `${sizePixels}px`);
    return;
  }

  const scrollLength = Math.abs(emblaApi.internalEngine().limit.length);
  const startDistance = emblaApi.scrollProgress() * scrollLength;
  const endDistance = (1 - emblaApi.scrollProgress()) * scrollLength;

  viewportElement.style.setProperty(
    "--gsa-fade-start",
    `${getEdgeFadePixels({ distance: startDistance, revealThresholdPixels, sizePixels })}px`,
  );
  viewportElement.style.setProperty(
    "--gsa-fade-end",
    `${getEdgeFadePixels({ distance: endDistance, revealThresholdPixels, sizePixels })}px`,
  );
}

/**
 * Per-slide CSS variable (`0`–`1`) expressing how close a slide is to the
 * viewport's center along the scroll axis: `1` when its center is aligned with
 * the viewport center, decaying to `0` one slide-length away. Read it from a
 * slide (or any descendant) to drive scroll-linked effects in pure CSS, e.g.
 * `scale: calc(1 + 0.08 * var(--carousel-slide-proximity, 0))`.
 */
const SLIDE_PROXIMITY_VARIABLE = "--carousel-slide-proximity";

function setSlideProximityVariables({
  emblaApi,
  orientation,
}: {
  emblaApi: EmblaCarouselType;
  orientation: CarouselOrientation;
}) {
  const isHorizontal = orientation === "horizontal";
  const viewportRect = emblaApi.rootNode().getBoundingClientRect();
  const viewportCenter = isHorizontal
    ? viewportRect.left + viewportRect.width / 2
    : viewportRect.top + viewportRect.height / 2;

  emblaApi.slideNodes().forEach((slide) => {
    const slideRect = slide.getBoundingClientRect();
    const slideLength = isHorizontal ? slideRect.width : slideRect.height;
    if (slideLength === 0) {
      return;
    }

    const slideCenter = isHorizontal
      ? slideRect.left + slideRect.width / 2
      : slideRect.top + slideRect.height / 2;
    const distance = Math.abs(slideCenter - viewportCenter);
    const proximity = Math.max(0, 1 - distance / slideLength);
    slide.style.setProperty(SLIDE_PROXIMITY_VARIABLE, proximity.toFixed(4));
  });
}

type CarouselRootProps<ClassName extends string = string> = Omit<
  useRender.ComponentProps<"div">,
  "className"
> &
  CarouselClassNameProps<ClassName> & {
    orientation?: CarouselOrientation;
    loop?: boolean;
    align?: EmblaOptionsType["align"];
    dragFree?: boolean;
    slidesToScroll?: EmblaOptionsType["slidesToScroll"];
    startIndex?: number;
    duration?: number;
    wheelGestures?: boolean | Partial<WheelGesturesPluginOptions>;
    autoplay?: boolean | AutoplayOptionsType;
    autoScroll?: boolean | AutoScrollOptionsType;
    options?: EmblaOptionsType;
    plugins?: EmblaPluginType[];
    index?: number;
    onIndexChange?: (index: number) => void;
    /**
     * Write a `--carousel-slide-proximity` CSS variable (`0`–`1`) on each slide
     * as it nears the viewport center, for scroll-linked CSS effects (e.g.
     * scaling the centered slide). Off by default — it measures slides on every
     * scroll frame.
     */
    trackSlideProximity?: boolean;
  };

const CarouselRootInner = <ClassName extends string>(
  {
    align = "start",
    autoScroll,
    autoplay,
    className,
    duration,
    dragFree,
    index,
    loop = false,
    onIndexChange,
    options,
    orientation = "horizontal",
    plugins,
    render,
    slidesToScroll,
    startIndex,
    trackSlideProximity = false,
    UNSAFE_className,
    wheelGestures,
    ...props
  }: CarouselRootProps<ClassName>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const shouldReduceMotion = Boolean(useReducedMotion());
  // Embla merges options by overwriting its defaults with every key present on
  // the passed object, including keys whose value is `undefined`. Spreading
  // omitted props directly would clobber Embla's defaults with `undefined`
  // (e.g. `duration` becomes `NaN`, breaking scroll math), so only include keys
  // that are actually set.
  const carouselOptions = React.useMemo<EmblaOptionsType>(
    () => ({
      axis: orientation === "horizontal" ? "x" : "y",
      align,
      containScroll: "trimSnaps",
      loop,
      ...(dragFree !== undefined && { dragFree }),
      ...(slidesToScroll !== undefined && { slidesToScroll }),
      ...(startIndex !== undefined && { startIndex }),
      ...(duration !== undefined && { duration }),
      ...options,
    }),
    [
      align,
      dragFree,
      duration,
      loop,
      options,
      orientation,
      slidesToScroll,
      startIndex,
    ],
  );
  const [carouselState, setCarouselState] =
    React.useState<CarouselState>(initialCarouselState);
  const isScrollable = carouselState.canGoPrevious || carouselState.canGoNext;
  // `isScrollable` is unknown until Embla measures, so the wheel plugin is
  // omitted on the first render and added via reInit (a plugins-array change)
  // once there's somewhere to scroll. This is stable: reInit recomputes the same
  // scrollability, so the plugins array stops changing.
  const carouselPlugins = React.useMemo(() => {
    return getPlugins({
      autoScroll,
      autoplay,
      isScrollable,
      plugins,
      shouldReduceMotion,
      wheelGestures,
    });
  }, [
    autoScroll,
    autoplay,
    isScrollable,
    plugins,
    shouldReduceMotion,
    wheelGestures,
  ]);
  const [viewportRef, emblaApi] = useEmblaCarousel(
    carouselOptions,
    carouselPlugins,
  );
  // Dragging is on by default in Embla; it's only off when `watchDrag` is
  // explicitly disabled via `options`.
  const isDraggable = options?.watchDrag !== false;

  // Init/reInit/resize sync selection state without notifying `onIndexChange` —
  // they fire even when the index didn't change (e.g. on resize).
  const syncSelectionState = React.useCallback(
    (nextEmblaApi: EmblaCarouselType) => {
      setCarouselState((previousState) => ({
        ...getCarouselSelectionState(nextEmblaApi),
        isDragging: previousState.isDragging,
      }));
    },
    [],
  );

  // Embla emits `select` only when the selected index actually changes, so
  // `onIndexChange` fires exactly on real changes without tracking the previous
  // index ourselves.
  const handleSelect = React.useCallback(
    (nextEmblaApi: EmblaCarouselType) => {
      const nextSelectionState = getCarouselSelectionState(nextEmblaApi);
      setCarouselState((previousState) => ({
        ...nextSelectionState,
        isDragging: previousState.isDragging,
      }));
      onIndexChange?.(nextSelectionState.selectedIndex);
    },
    [onIndexChange],
  );

  // Subscribes to Embla's imperative event emitter, which requires an effect.
  // eslint-disable-next-line replo/no-use-effect
  React.useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const handlePointerDown = () => {
      setCarouselState((previousState) => ({
        ...previousState,
        isDragging: true,
      }));
    };
    const handlePointerUp = () => {
      setCarouselState((previousState) => ({
        ...previousState,
        isDragging: false,
      }));
    };

    syncSelectionState(emblaApi);
    emblaApi.on("init", syncSelectionState);
    emblaApi.on("reInit", syncSelectionState);
    emblaApi.on("select", handleSelect);
    emblaApi.on("pointerDown", handlePointerDown);
    emblaApi.on("pointerUp", handlePointerUp);

    return () => {
      emblaApi.off("init", syncSelectionState);
      emblaApi.off("reInit", syncSelectionState);
      emblaApi.off("select", handleSelect);
      emblaApi.off("pointerDown", handlePointerDown);
      emblaApi.off("pointerUp", handlePointerUp);
    };
  }, [emblaApi, syncSelectionState, handleSelect]);

  // Bridges the controlled `index` prop to Embla's imperative scrollTo API.
  // eslint-disable-next-line replo/no-use-effect
  React.useEffect(() => {
    if (!emblaApi || isNullish(index)) {
      return;
    }

    if (emblaApi.selectedScrollSnap() !== index) {
      emblaApi.scrollTo(index, shouldReduceMotion);
    }
  }, [emblaApi, index, shouldReduceMotion]);

  // Writes the per-slide proximity variable from Embla's scroll position. Reads
  // and writes DOM imperatively in response to Embla's event emitter.
  // eslint-disable-next-line replo/no-use-effect
  React.useEffect(() => {
    if (!trackSlideProximity || !emblaApi) {
      return;
    }

    const updateSlideProximity = () => {
      setSlideProximityVariables({ emblaApi, orientation });
    };

    updateSlideProximity();
    emblaApi.on("scroll", updateSlideProximity);
    emblaApi.on("reInit", updateSlideProximity);
    emblaApi.on("resize", updateSlideProximity);
    emblaApi.on("settle", updateSlideProximity);
    emblaApi.on("slidesChanged", updateSlideProximity);

    return () => {
      emblaApi.off("scroll", updateSlideProximity);
      emblaApi.off("reInit", updateSlideProximity);
      emblaApi.off("resize", updateSlideProximity);
      emblaApi.off("settle", updateSlideProximity);
      emblaApi.off("slidesChanged", updateSlideProximity);
    };
  }, [emblaApi, orientation, trackSlideProximity]);

  const goPrevious = React.useCallback(() => {
    if (!emblaApi) {
      return;
    }
    interruptAutoMotion(emblaApi);
    emblaApi.scrollPrev(shouldReduceMotion);
  }, [emblaApi, shouldReduceMotion]);

  const goNext = React.useCallback(() => {
    if (!emblaApi) {
      return;
    }
    interruptAutoMotion(emblaApi);
    emblaApi.scrollNext(shouldReduceMotion);
  }, [emblaApi, shouldReduceMotion]);

  const goTo = React.useCallback(
    (nextIndex: number) => {
      if (!emblaApi) {
        return;
      }
      interruptAutoMotion(emblaApi);
      emblaApi.scrollTo(nextIndex, shouldReduceMotion);
    },
    [emblaApi, shouldReduceMotion],
  );

  const contextValue = React.useMemo<CarouselContextValue>(
    () => ({
      ...carouselState,
      emblaApi,
      goNext,
      goPrevious,
      goTo,
      isDraggable,
      isScrollable,
      loop,
      orientation,
      viewportRef,
    }),
    [
      carouselState,
      emblaApi,
      goNext,
      goPrevious,
      goTo,
      isDraggable,
      isScrollable,
      loop,
      orientation,
      viewportRef,
    ],
  );

  return (
    <CarouselContext.Provider value={contextValue}>
      {useRender({
        defaultTagName: "div",
        ref,
        props: mergeProps<"div">(
          {
            role: "region",
            "aria-roledescription": "carousel",
            className: cn(className, UNSAFE_className),
            onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
              if (isTextEntryTarget(event.target)) {
                return;
              }

              const previousKey =
                orientation === "horizontal" ? "ArrowLeft" : "ArrowUp";
              const nextKey =
                orientation === "horizontal" ? "ArrowRight" : "ArrowDown";

              if (event.key === previousKey) {
                event.preventDefault();
                goPrevious();
              }

              if (event.key === nextKey) {
                event.preventDefault();
                goNext();
              }
            },
          },
          props,
        ),
        render,
        state: {
          slot: "carousel",
          orientation,
        },
      })}
    </CarouselContext.Provider>
  );
};

// eslint-disable-next-line replo/no-as-cast
const CarouselRoot = React.forwardRef(CarouselRootInner) as <
  ClassName extends string,
>(
  props: CarouselRootProps<ClassName> & React.RefAttributes<HTMLDivElement>,
) => React.ReactNode;

type CarouselViewportProps<ClassName extends string = string> = Omit<
  useRender.ComponentProps<"div">,
  "className"
> &
  CarouselClassNameProps<ClassName> & {
    edgeFade?: boolean;
    /** Width of the faded band at each edge, in pixels. Defaults to `32`. */
    edgeFadeSizePixels?: number;
    /**
     * Scroll distance, in pixels, over which the fade grows from nothing to its
     * full width at each edge. Defaults to `16`.
     */
    edgeFadeRevealThresholdPixels?: number;
  };

const edgeFadeClassByOrientation: Record<CarouselOrientation, string> = {
  horizontal: "carousel-scroll-fade carousel-scroll-fade--horizontal",
  vertical: "carousel-scroll-fade carousel-scroll-fade--vertical",
};

const viewportClassByOrientation: Record<CarouselOrientation, string> = {
  horizontal: "overflow-hidden",
  vertical: "overflow-hidden",
};

const CarouselViewportInner = <ClassName extends string>(
  {
    className,
    edgeFade = false,
    edgeFadeRevealThresholdPixels = 16,
    edgeFadeSizePixels = 32,
    render,
    style,
    UNSAFE_className,
    ...props
  }: CarouselViewportProps<ClassName>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const carousel = useCarousel();
  const viewportElementRef = React.useRef<HTMLElement | null>(null);

  const setViewportElement = React.useCallback(
    (element: HTMLElement | null) => {
      viewportElementRef.current = element;
      carousel.viewportRef(element);
    },
    [carousel.viewportRef],
  );

  // Driven by Embla's imperative scroll events and direct DOM style writes.
  // eslint-disable-next-line replo/no-use-effect
  React.useEffect(() => {
    const emblaApi = carousel.emblaApi;
    const viewportElement = viewportElementRef.current;
    if (!edgeFade || !emblaApi || !viewportElement) {
      return;
    }

    const updateEdgeFade = () => {
      setEdgeFadeVariables({
        emblaApi,
        isScrollable: carousel.isScrollable,
        revealThresholdPixels: edgeFadeRevealThresholdPixels,
        sizePixels: edgeFadeSizePixels,
        viewportElement,
      });
    };

    updateEdgeFade();
    emblaApi.on("scroll", updateEdgeFade);
    emblaApi.on("select", updateEdgeFade);
    emblaApi.on("reInit", updateEdgeFade);
    emblaApi.on("settle", updateEdgeFade);

    return () => {
      emblaApi.off("scroll", updateEdgeFade);
      emblaApi.off("select", updateEdgeFade);
      emblaApi.off("reInit", updateEdgeFade);
      emblaApi.off("settle", updateEdgeFade);
    };
  }, [
    carousel.emblaApi,
    carousel.isScrollable,
    edgeFade,
    edgeFadeRevealThresholdPixels,
    edgeFadeSizePixels,
  ]);

  // When the carousel is draggable and has somewhere to scroll, surface a grab
  // affordance and suppress text selection automatically — switching to the
  // closed-hand cursor while the user is actively dragging.
  const showGrabCursor = carousel.isDraggable && carousel.isScrollable;

  return useRender({
    defaultTagName: "div",
    ref: [setViewportElement, ref],
    props: mergeProps<"div">(
      {
        className: cn(
          viewportClassByOrientation[carousel.orientation],
          edgeFade && edgeFadeClassByOrientation[carousel.orientation],
          showGrabCursor && "cursor-grab select-none",
          showGrabCursor && carousel.isDragging && "cursor-grabbing",
          className,
          UNSAFE_className,
        ),
        style,
      },
      props,
    ),
    render,
    state: {
      slot: "carousel-viewport",
      orientation: carousel.orientation,
      edgeFade,
    },
  });
};

// eslint-disable-next-line replo/no-as-cast
const CarouselViewport = React.forwardRef(CarouselViewportInner) as <
  ClassName extends string,
>(
  props: CarouselViewportProps<ClassName> & React.RefAttributes<HTMLDivElement>,
) => React.ReactNode;

type CarouselContentProps<ClassName extends string = string> = Omit<
  useRender.ComponentProps<"div">,
  "className"
> &
  CarouselClassNameProps<ClassName>;

const contentClassByOrientation: Record<CarouselOrientation, string> = {
  horizontal: "flex touch-pan-y",
  vertical: "flex flex-col touch-pan-x",
};

const CarouselContentInner = <ClassName extends string>(
  {
    className,
    render,
    UNSAFE_className,
    ...props
  }: CarouselContentProps<ClassName>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const carousel = useCarousel();

  return useRender({
    defaultTagName: "div",
    ref,
    props: mergeProps<"div">(
      {
        className: cn(
          contentClassByOrientation[carousel.orientation],
          className,
          UNSAFE_className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "carousel-content",
      orientation: carousel.orientation,
    },
  });
};

// eslint-disable-next-line replo/no-as-cast
const CarouselContent = React.forwardRef(CarouselContentInner) as <
  ClassName extends string,
>(
  props: CarouselContentProps<ClassName> & React.RefAttributes<HTMLDivElement>,
) => React.ReactNode;

type CarouselSlideProps<ClassName extends string = string> = Omit<
  useRender.ComponentProps<"div">,
  "className"
> &
  CarouselClassNameProps<ClassName>;

const CarouselSlideInner = <ClassName extends string>(
  {
    className,
    render,
    UNSAFE_className,
    ...props
  }: CarouselSlideProps<ClassName>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  return useRender({
    defaultTagName: "div",
    ref,
    props: mergeProps<"div">(
      {
        role: "group",
        "aria-roledescription": "slide",
        className: cn(
          "min-w-0 shrink-0 grow-0 basis-auto",
          className,
          UNSAFE_className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "carousel-slide",
    },
  });
};

// The per-call `ClassName` generic is what lets `LayoutClassName<ClassName>`
// validate the exact className literal at each call site (`LayoutClassName<string>`
// collapses to the error type, so a non-generic prop is unusable). `forwardRef`'s
// type erases that generic — it instantiates the render function once — so the
// only way to keep a generic call signature while forwarding a ref in React 18 is
// to re-assert it here. (React 19's ref-as-prop would remove the need for
// `forwardRef`, and the cast with it.)
// eslint-disable-next-line replo/no-as-cast
const CarouselSlide = React.forwardRef(CarouselSlideInner) as <
  ClassName extends string,
>(
  props: CarouselSlideProps<ClassName> & React.RefAttributes<HTMLDivElement>,
) => React.ReactNode;

type CarouselButtonProps<ClassName extends string = string> = Omit<
  useRender.ComponentProps<"button">,
  "className"
> &
  CarouselClassNameProps<ClassName>;

function CarouselPreviousButton<ClassName extends string>({
  className,
  render,
  UNSAFE_className,
  ...props
}: CarouselButtonProps<ClassName>) {
  const carousel = useCarousel();
  const disabled = !carousel.canGoPrevious;

  return useRender({
    defaultTagName: "button",
    props: mergeProps<"button">(
      {
        ...(!render && { type: "button" as const }),
        "aria-label": "Previous slide",
        disabled,
        className: cn(className, UNSAFE_className),
        onClick: () => {
          carousel.goPrevious();
        },
      },
      props,
    ),
    render,
    state: {
      slot: "carousel-previous",
      disabled,
    },
  });
}

function CarouselNextButton<ClassName extends string>({
  className,
  render,
  UNSAFE_className,
  ...props
}: CarouselButtonProps<ClassName>) {
  const carousel = useCarousel();
  const disabled = !carousel.canGoNext;

  return useRender({
    defaultTagName: "button",
    props: mergeProps<"button">(
      {
        ...(!render && { type: "button" as const }),
        "aria-label": "Next slide",
        disabled,
        className: cn(className, UNSAFE_className),
        onClick: () => {
          carousel.goNext();
        },
      },
      props,
    ),
    render,
    state: {
      slot: "carousel-next",
      disabled,
    },
  });
}

type CarouselIndicatorsProps<
  ClassName extends string = string,
  IndicatorClassName extends string = string,
> = Omit<useRender.ComponentProps<"div">, "className" | "children"> &
  CarouselClassNameProps<ClassName> & {
    getIndicatorAriaLabel?: (index: number) => string;
    indicatorClassName?: LayoutClassName<IndicatorClassName>;
    UNSAFE_indicatorClassName?: string;
  };

function CarouselIndicators<
  ClassName extends string,
  IndicatorClassName extends string,
>({
  className,
  getIndicatorAriaLabel = (index) => `Go to slide ${index + 1}`,
  indicatorClassName,
  render,
  UNSAFE_className,
  UNSAFE_indicatorClassName,
  ...props
}: CarouselIndicatorsProps<ClassName, IndicatorClassName>) {
  const carousel = useCarousel();

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(
      {
        className: cn(className, UNSAFE_className),
        children: carousel.scrollSnaps.map((_, index) => {
          const active = index === carousel.selectedIndex;
          return (
            <button
              key={index}
              type="button"
              data-slot="carousel-indicator"
              data-active={active ? "" : undefined}
              aria-current={active ? "true" : undefined}
              aria-label={getIndicatorAriaLabel(index)}
              className={cn(indicatorClassName, UNSAFE_indicatorClassName)}
              onClick={() => {
                carousel.goTo(index);
              }}
            />
          );
        }),
      },
      props,
    ),
    render,
    state: {
      slot: "carousel-indicators",
    },
  });
}

type CarouselProps<
  ClassName extends string = string,
  ViewportClassName extends string = string,
  ContentClassName extends string = string,
  SlideClassName extends string = string,
> = React.PropsWithChildren<
  Omit<CarouselRootProps<ClassName>, "children"> & {
    edgeFade?: boolean;
    edgeFadeSizePixels?: number;
    edgeFadeRevealThresholdPixels?: number;
    viewportClassName?: LayoutClassName<ViewportClassName>;
    UNSAFE_viewportClassName?: string;
    contentClassName?: LayoutClassName<ContentClassName>;
    UNSAFE_contentClassName?: string;
    slideClassName?: LayoutClassName<SlideClassName>;
    UNSAFE_slideClassName?: string;
  }
>;

function Carousel<
  ClassName extends string,
  ViewportClassName extends string,
  ContentClassName extends string,
  SlideClassName extends string,
>({
  children,
  contentClassName,
  edgeFade,
  edgeFadeRevealThresholdPixels,
  edgeFadeSizePixels,
  slideClassName,
  UNSAFE_contentClassName,
  UNSAFE_slideClassName,
  UNSAFE_viewportClassName,
  viewportClassName,
  ...props
}: CarouselProps<
  ClassName,
  ViewportClassName,
  ContentClassName,
  SlideClassName
>) {
  return (
    <CarouselRoot {...props}>
      <CarouselViewport
        edgeFade={edgeFade}
        edgeFadeRevealThresholdPixels={edgeFadeRevealThresholdPixels}
        edgeFadeSizePixels={edgeFadeSizePixels}
        className={viewportClassName}
        UNSAFE_className={UNSAFE_viewportClassName}
      >
        <CarouselContent
          className={contentClassName}
          UNSAFE_className={UNSAFE_contentClassName}
        >
          {React.Children.map(children, (child) => (
            <CarouselSlide
              className={slideClassName}
              UNSAFE_className={UNSAFE_slideClassName}
            >
              {child}
            </CarouselSlide>
          ))}
        </CarouselContent>
      </CarouselViewport>
    </CarouselRoot>
  );
}

export {
  Carousel,
  CarouselContent,
  CarouselIndicators,
  CarouselNextButton,
  CarouselPreviousButton,
  CarouselRoot,
  CarouselSlide,
  CarouselViewport,
  useCarousel,
};
export type {
  CarouselContentProps,
  CarouselIndicatorsProps,
  CarouselOrientation,
  CarouselProps,
  CarouselRootProps,
  CarouselSlideProps,
  CarouselViewportProps,
};
